import { useAppTranslation } from '@tebuto/functions/translation.page'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { array, boolean, number, object, string } from 'yup'

const MINUTES_PER_HOUR = 60
const HOURS_PER_DAY = 24

export const DEFAULT_GENERATOR_BREAK_MINUTES = 10
export const DEFAULT_GENERATOR_END_HOUR = 17
export const MAX_GENERATED_EVENT_RULES = 48
export const MAX_GENERATOR_BREAK_MINUTES = HOURS_PER_DAY * MINUTES_PER_HOUR

export const EVENT_RULE_CREATION_MODES = {
    Single: 'single',
    Generator: 'generator'
} as const

export type EventRuleCreationMode = (typeof EVENT_RULE_CREATION_MODES)[keyof typeof EVENT_RULE_CREATION_MODES]
export type NumericInputValue = number | string

export interface GeneratedEventRuleSlot {
    start: DateTime<true>
    end: DateTime<true>
}

export interface EventRuleCategoryDurationOption {
    id: number
    duration: number
}

export interface EventRuleForm {
    mode: EventRuleCreationMode
    categoryIds: number[]
    startTime: DateTime<true>
    startDate: DateTime<true>
    endDate: DateTime<true> | null
    interval: number
    customIntervalSelected: boolean
    customInterval: string | null
    generatorEndTime: DateTime<true>
    generatorBreakMinutes: NumericInputValue
    conflict: boolean // to only enable the submit button if there are no conflicts
}

function isGeneratorModeValue(mode: unknown): boolean {
    const value = Array.isArray(mode) ? mode[0] : mode
    return value === EVENT_RULE_CREATION_MODES.Generator
}

export function useEventRuleFormValidationSchema() {
    const { t } = useAppTranslation()

    const eventRuleFormValidationSchema = useMemo(
        () =>
            object().shape({
                mode: string().required(),
                categoryIds: array()
                    .of(number().required())
                    .min(1, t('calendarPage.editEventRuleModal.errors.categoryRequired'))
                    .required(t('calendarPage.editEventRuleModal.errors.categoryRequired')),
                startTime: string().required(t('calendarPage.editEventRuleModal.errors.timeStartRequired')),
                startDate: string().required(t('calendarPage.editEventRuleModal.errors.dateStartRequired')),
                endDate: string().nullable(),
                interval: number().required(t('calendarPage.editEventRuleModal.errors.intervalRequired')),
                generatorEndTime: string().when('mode', (mode, schema) =>
                    isGeneratorModeValue(mode)
                        ? schema.required(t('calendarPage.addEventRuleModal.generator.errors.endTimeRequired'))
                        : schema
                ),
                generatorBreakMinutes: number().when('mode', (mode, schema) =>
                    isGeneratorModeValue(mode)
                        ? schema
                              .typeError(t('calendarPage.addEventRuleModal.generator.errors.breakRequired'))
                              .integer(t('calendarPage.addEventRuleModal.generator.errors.breakInteger'))
                              .min(0, t('calendarPage.addEventRuleModal.generator.errors.breakMin'))
                              .max(
                                  MAX_GENERATOR_BREAK_MINUTES,
                                  t('calendarPage.addEventRuleModal.generator.errors.breakMax')
                              )
                              .required(t('calendarPage.addEventRuleModal.generator.errors.breakRequired'))
                        : schema
                ),
                conflict: boolean().oneOf([false], t('calendarPage.addEventRuleModal.conflictError'))
            }),
        []
    )

    return eventRuleFormValidationSchema
}

export function useIntervalOptions() {
    const { t } = useAppTranslation()
    return (day: string) =>
        [
            { frequency: '0', label: t('calendarPage.interval.0') },
            { frequency: '1', label: t('calendarPage.interval.1', { day }) },
            { frequency: '2', label: t('calendarPage.interval.2', { day }) },
            { frequency: '3', label: t('calendarPage.interval.3', { day }) },
            { frequency: '4', label: t('calendarPage.interval.4', { day }) }
        ].map((interval) => ({ value: interval.frequency, label: interval.label }))
}

export function getEffectiveEventRuleInterval(values: EventRuleForm): number {
    return values.customIntervalSelected && values.customInterval
        ? Number.parseInt(values.customInterval, 10)
        : values.interval
}

export function getSelectedEventRuleCategoryIds(values: EventRuleForm): number[] {
    return [...new Set(values.categoryIds.map((categoryId) => Number(categoryId)))]
}

export function getLongestSelectedEventRuleCategoryDuration(
    values: EventRuleForm,
    categories: EventRuleCategoryDurationOption[]
): number {
    const selectedCategoryIds = new Set(getSelectedEventRuleCategoryIds(values))
    return categories.reduce(
        (longestDuration, category) =>
            selectedCategoryIds.has(category.id) && category.duration > longestDuration
                ? category.duration
                : longestDuration,
        0
    )
}

export function getEventRuleStartDateTime(
    values: EventRuleForm,
    startTime: DateTime<true> = values.startTime
): DateTime<true> {
    return values.startDate
        .set({
            hour: startTime.hour,
            minute: startTime.minute,
            second: 0,
            millisecond: 0
        })
        .toLocal() as DateTime<true>
}

export function getGeneratorBreakMinutes(values: EventRuleForm): number {
    const breakMinutes =
        typeof values.generatorBreakMinutes === 'string'
            ? Number.parseInt(values.generatorBreakMinutes, 10)
            : values.generatorBreakMinutes

    return Number.isFinite(breakMinutes) ? breakMinutes : Number.NaN
}

export function getGeneratorEndDateTime(values: EventRuleForm): DateTime<true> {
    return values.startDate
        .set({
            hour: values.generatorEndTime.hour,
            minute: values.generatorEndTime.minute,
            second: 0,
            millisecond: 0
        })
        .toLocal() as DateTime<true>
}

export function buildGeneratedEventRuleSlots(values: EventRuleForm, durationMinutes: number): GeneratedEventRuleSlot[] {
    const breakMinutes = getGeneratorBreakMinutes(values)
    const start = getEventRuleStartDateTime(values)
    const generatorEnd = getGeneratorEndDateTime(values)

    if (durationMinutes <= 0 || breakMinutes < 0 || !Number.isFinite(breakMinutes) || generatorEnd <= start) {
        return []
    }

    const slots: GeneratedEventRuleSlot[] = []
    const stepMinutes = durationMinutes + breakMinutes
    let slotStart = start

    while (slots.length < MAX_GENERATED_EVENT_RULES) {
        const slotEnd = slotStart.plus({ minutes: durationMinutes }) as DateTime<true>
        if (slotEnd > generatorEnd) {
            break
        }

        slots.push({ start: slotStart, end: slotEnd })
        slotStart = slotStart.plus({ minutes: stepMinutes }) as DateTime<true>
    }

    return slots
}

export function isGeneratedEventRuleLimitExceeded(values: EventRuleForm, durationMinutes: number): boolean {
    const breakMinutes = getGeneratorBreakMinutes(values)
    const start = getEventRuleStartDateTime(values)
    const generatorEnd = getGeneratorEndDateTime(values)

    if (durationMinutes <= 0 || breakMinutes < 0 || !Number.isFinite(breakMinutes) || generatorEnd <= start) {
        return false
    }

    const stepMinutes = durationMinutes + breakMinutes
    const nextStartAfterLimit = start.plus({ minutes: stepMinutes * MAX_GENERATED_EVENT_RULES }) as DateTime<true>
    const nextEndAfterLimit = nextStartAfterLimit.plus({ minutes: durationMinutes }) as DateTime<true>

    return nextEndAfterLimit <= generatorEnd
}
